/**
 * Help Center - Comprehensive Support Hub
 * Clean, focused layout with page-based article navigation
 */

import type { LoaderFunctionArgs } from 'react-router';
import { useLoaderData, useSearchParams, useNavigation, Link } from 'react-router';
import {
  Page,
  BlockStack,
  Card,
  Text,
  InlineStack,
  Box,
  Icon,
  SkeletonPage,
  SkeletonBodyText,
  SkeletonDisplayText,
} from '@shopify/polaris';
import {
  BookIcon,
  SettingsIcon,
  AlertCircleIcon,
  PlayIcon,
  LayoutColumns3Icon,
  AlertBubbleIcon,
  CalculatorIcon,
  NotificationIcon,
  CalendarIcon,
  RefreshIcon,
  BugIcon,
  CodeIcon,
  EmailIcon,
  FileIcon,
  ChevronRightIcon,
} from '@shopify/polaris-icons';
import type { Env } from '../../src/types';
import { getArticlesByCategory } from '../utils/help-center';
import { useLoading } from '../hooks/shopify';
import {
  FeaturesBanner,
  ConfigurationBanner,
  TroubleshootingBanner,
} from '../components/help/banners';
import { getEnv } from '~/utils/cloudflare-context';

interface LoaderData {
  categories: {
    configuration: any[];
    features: any[];
    troubleshooting: any[];
  };
}

// Map article slugs to icons
const articleIcons: Record<string, any> = {
  'quick-setup': PlayIcon,
  'dashboard-overview': LayoutColumns3Icon,
  'alert-thresholds': AlertBubbleIcon,
  'notifications': NotificationIcon,
  'seasonal-adjustments': CalendarIcon,
  'velocity-calculations': CalculatorIcon,
  'smart-alerts': AlertCircleIcon,
  'inventory-tracking': RefreshIcon,
  'cloudflare-infrastructure': CodeIcon,
  'missing-alerts': BugIcon,
  'incorrect-predictions': SettingsIcon,
};

export async function loader({ context, request }: LoaderFunctionArgs) {
  const env = getEnv(context);
  const url = new URL(request.url);
  const shop = url.searchParams.get('shop');

  // Track help center visit (silent failure)
  if (shop) {
    try {
      const shopResult = await env.DB.prepare('SELECT id FROM shops WHERE shop_domain = ?').bind(shop).first();

      if (shopResult) {
        await env.DB.prepare(
          `INSERT INTO help_center_views (shop_id, article_slug, article_title, session_id)
           VALUES (?, ?, ?, ?)`
        )
          .bind(shopResult.id, 'help-index', 'Help Center Home', 'session-' + Date.now())
          .run();
      }
    } catch (error) {
      // Visit tracking failed - non-critical analytics, silent fail
    }
  }

  return {
    categories: {
      configuration: getArticlesByCategory('configuration'),
      features: getArticlesByCategory('features'),
      troubleshooting: getArticlesByCategory('troubleshooting'),
    },
  };
}

export default function HelpCenterIndex() {
  const { categories } = useLoaderData<LoaderData>();
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const queryString = searchParams.toString();
  
  // Page loading state
  const isPageLoading = navigation.state === 'loading';
  
  // Control App Bridge loading indicator
  useLoading(isPageLoading);

  // Show skeleton page while loading
  if (isPageLoading) {
    return (
      <SkeletonPage
        title="Help Center"
        backAction
        primaryAction
      >
        <BlockStack gap="500">
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={4} />
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={4} />
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={4} />
            </BlockStack>
          </Card>
        </BlockStack>
      </SkeletonPage>
    );
  }

  const quickLinks = [
    {
      title: 'Features',
      description: 'Learn what Stock Sentry can do',
      articles: categories.features,
      Banner: FeaturesBanner,
    },
    {
      title: 'Configuration',
      description: 'Customize alerts and settings',
      articles: categories.configuration,
      Banner: ConfigurationBanner,
    },
    {
      title: 'Troubleshooting',
      description: 'Resolve common issues',
      articles: categories.troubleshooting,
      Banner: TroubleshootingBanner,
    },
  ];

  return (
    <div
      className="page-enter"
      style={{
        backgroundColor: '#f4f4f6',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      {/* Page background - bottom-left soft beams */}
      <div className="page-bg-help" aria-hidden="true" />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Page
          title="Help Center"
          subtitle="Find answers and get support"
          backAction={{
            content: 'Dashboard',
            url: `/app${queryString ? `?${queryString}` : ''}`,
          }}
          primaryAction={{
            content: 'Submit Ticket',
            icon: EmailIcon,
            url: `/app/support${queryString ? `?${queryString}` : ''}`,
          }}
          secondaryActions={[
            {
              content: 'View FAQ',
              icon: FileIcon,
              url: `/app/help/faq${queryString ? `?${queryString}` : ''}`,
            },
          ]}
        >
          <BlockStack gap="500">

            {/* Help Categories - Merged Header with Illustration */}
            {quickLinks.map((category, index) => {
              const CategoryBanner = category.Banner;
              return (
              <div key={category.title} className="slide-in-up" style={{ animationDelay: `${(index + 1) * 50}ms` }}>
                <Card>
                  <BlockStack gap="400">
                    {/* Merged header: Illustration + Title/Description */}
                    <InlineStack gap="400" blockAlign="center" wrap={false}>
                      <div className="help-category-illustration">
                        <CategoryBanner width={72} height={72} />
                      </div>
                      <BlockStack gap="100">
                        <Text as="h2" variant="headingLg" fontWeight="semibold">
                          {category.title}
                        </Text>
                        <Text as="p" variant="bodyMd" tone="subdued">
                          {category.description}
                        </Text>
                      </BlockStack>
                    </InlineStack>

                    {/* Article list */}
                    <BlockStack gap="200">
                      {category.articles.slice(0, 5).map((article: any) => {
                        const ArticleIcon = articleIcons[article.slug] || BookIcon;
                        return (
                          <Link
                            key={article.slug}
                            to={`/app/help/${article.slug}${queryString ? `?${queryString}` : ''}`}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            <Box
                              paddingBlock="300"
                              paddingInline="400"
                              borderRadius="200"
                              background="bg-surface-hover"
                            >
                              <InlineStack align="space-between" blockAlign="center" wrap={false}>
                                <InlineStack gap="300" blockAlign="center">
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '20px',
                                    height: '20px',
                                  }}>
                                    <Icon source={ArticleIcon} tone="subdued" />
                                  </div>
                                  <Text as="p" variant="bodyMd">
                                    {article.title}
                                  </Text>
                                </InlineStack>
                                <InlineStack gap="200" blockAlign="center">
                                  <Text as="span" variant="bodySm" tone="subdued">
                                    {article.readTime} min read
                                  </Text>
                                  <Icon source={ChevronRightIcon} tone="subdued" />
                                </InlineStack>
                              </InlineStack>
                            </Box>
                          </Link>
                        );
                      })}
                    </BlockStack>

                    {/* View all articles link */}
                    {category.articles.length > 5 && (
                      <Box paddingBlockStart="200">
                        <Text as="p" variant="bodySm" tone="subdued">
                          {category.articles.length - 5} more articles available
                        </Text>
                      </Box>
                    )}
                  </BlockStack>
                </Card>
              </div>
              );
            })}
          </BlockStack>
        </Page>
      </div>
    </div>
  );
}
